import { useEffect } from "react";
import cx from "classnames";
import { useLocalStorage } from "@rehooks/local-storage";
import { differenceInDays, fromUnixTime } from "date-fns/esm";
import { useLocation } from "react-router";
import { useShallow } from "zustand/react/shallow";

import AccessPolicyWarning from "components/AccessPolicyWarning";
import BurstBudgetCallout from "components/BurstBudgetCallout";
import DocumentationBar from "components/DocumentationBar";
import GlobalCallout from "components/GlobalCallout";
import MaintenanceCallout from "components/MaintenanceCallout";
import ScheduledMaintenanceCallout from "components/ScheduledMaintenanceCallout";
import useTypedContext from "hooks/useTypedContext";
import { AccountContext } from "views/AccountWrapper";
import { DOCUMENTATION_BAR_CLOSED } from "constants/localStorageKeys";
import { getNowTimeMs } from "utils/time";
import SelfHostedLicenseWarning from "components/SelfHostedLicenseWarning";
import MainNavigation from "components/MainNavigation";
import MainNavigationMobile from "components/MainNavigation/components/layout/Mobile";
import useMainNavigationStore from "components/MainNavigation/stores/useMainNavigationStore";
import { selectNavigationIsExpanded } from "components/MainNavigation/selectors/navigationState";
import CommandPalette from "components/CommandPalette";
import AiChat from "components/AssistantPanel";
import useAiChatStore from "components/AssistantPanel/Layout/useStore";
import useAssistantPanelAvailable from "hooks/useAssistantPanelAvailable";
import useIsAssistantPageMode from "components/AssistantPanel/hooks/useIsAssistantPageMode";
import useAssistantPanelExternalOpenTriggers from "components/AssistantPanel/hooks/useExternalOpenTriggers";

import useLayoutStore from "./stores/useLayoutStore";
import styles from "./styles.module.css";

type LayoutProps = {
  className?: string;
  children: React.ReactNode;
};

const Layout = ({ children, className }: LayoutProps) => {
  const isSidebarExpanded = useMainNavigationStore(selectNavigationIsExpanded);
  const { isAiChatOpen, aiChatVariant, aiChatWidth, isAiChatSidebarResizing } = useAiChatStore(
    useShallow((state) => ({
      isAiChatOpen: state.isOpen,
      aiChatVariant: state.variant,
      aiChatWidth: state.width,
      isAiChatSidebarResizing: state.isSidebarResizing,
    }))
  );

  const {
    viewer: { since, cannotEvaluateAccessPolicies },
  } = useTypedContext(AccountContext);
  const isAssistantPanelAvailable = useAssistantPanelAvailable();
  const location = useLocation();

  // Prevent rendering AiChat on AI dedicated page
  const isOnAssistantPage = useIsAssistantPageMode();

  const isAssistantPanelRendered = isAssistantPanelAvailable && !isOnAssistantPage;

  useAssistantPanelExternalOpenTriggers(isAssistantPanelRendered);

  const isAiChatSidebarOpen = isAssistantPanelRendered && isAiChatOpen && aiChatVariant === "side";

  const { setShowMobileMenu, setIsDocumentationBarVisible } = useLayoutStore(
    useShallow((state) => ({
      setShowMobileMenu: state.setShowMobileMenu,
      setIsDocumentationBarVisible: state.setIsDocumentationBarVisible,
    }))
  );

  const [documentationBarClosed] = useLocalStorage(DOCUMENTATION_BAR_CLOSED);

  const firstLoginTime = since ? fromUnixTime(since) : null;

  const shouldShowDocumentationBar =
    !documentationBarClosed &&
    firstLoginTime &&
    differenceInDays(getNowTimeMs(), firstLoginTime) < 14;

  const isDocumentationBarVisible = !!shouldShowDocumentationBar;

  useEffect(() => {
    setIsDocumentationBarVisible(isDocumentationBarVisible);
  }, [isDocumentationBarVisible, setIsDocumentationBarVisible]);

  useEffect(() => {
    setShowMobileMenu(false);
  }, [location, setShowMobileMenu]);

  return (
    <main
      className={cx(
        styles.layout,
        {
          [styles.expanded]: isSidebarExpanded,
          [styles.aiChatSidebarOpen]: isAiChatSidebarOpen,
          [styles.aiChatSidebarResizing]: isAiChatSidebarResizing,
        },
        className
      )}
      style={
        {
          ...(isAiChatSidebarOpen ? { "--ai-chat-sidebar-width": `${aiChatWidth}px` } : {}),
        } as React.CSSProperties
      }
    >
      <MainNavigation />

      {isAssistantPanelRendered && <AiChat />}

      <div id="layoutContent" className={styles.content}>
        <MaintenanceCallout />
        <ScheduledMaintenanceCallout />
        <GlobalCallout />
        {isDocumentationBarVisible && <DocumentationBar />}
        {cannotEvaluateAccessPolicies && <AccessPolicyWarning />}
        <SelfHostedLicenseWarning type="callout" storageKey="licenseWarningCallout" />
        <BurstBudgetCallout />
        {children}

        <MainNavigationMobile />
      </div>

      <CommandPalette />
    </main>
  );
};

export default Layout;
